/**
 * Prerender utilities using PerformanceObserver for network idle detection
 */

const BOT_USER_AGENTS = [
  'prerender',
  // Search Engines
  'googlebot',
  'adsbot-google',
  'apis-google',
  'mediapartners-google',
  'google-safety',
  'feedfetcher-google',
  'googleproducer',
  'google-site-verification',
  'bingbot',
  'yandexbot',
  'yabrowser',
  'yahoo',
  'baiduspider',
  'naver',
  'seznambot',
  'sznprohlizec',
  'qwantbot',
  'ecosia',
  'duckduckbot',
  'duckassistbot',
  'applebot',
  // Social Media
  'facebookexternalhit',
  'facebookcatalog',
  'facebookbot',
  'meta-externalagent',
  'twitterbot',
  'linkedinbot',
  'whatsapp',
  'slackbot',
  'pinterest',
  'pinterestbot',
  'tiktok',
  'tiktokspider',
  'bytespider',
  'discordbot',
  // SEO Tools
  'semrushbot',
  'ahrefsbot',
  'chrome-lighthouse',
  'screaming-frog',
  'oncrawlbot',
  'botifybot',
  'deepcrawl',
  'lumar',
  'rogerbot',
  'dotbot',
  // AI Bots
  'gptbot',
  'chatgpt',
  'oai-searchbot',
  'chatgpt-user',
  'claudebot',
  'google-extended',
  'perplexitybot',
  'perplexity-user',
  'youbot',
  'amazonbot',
  'anthropic-ai',
  'claude-web',
  'ccbot',
  'mistralai-user',
  // Other Known Bots & Crawlers
  'embedly',
  'quora link preview',
  'showyoubot',
  'outbrain',
  'pinterest/0.',
  'developers.google.com/+/web/snippet',
  'vkshare',
  'w3c_validator',
  'redditbot',
  'flipboard',
  'tumblr',
  'bitlybot',
  'skypeuripreview',
  'nuzzel',
  'google page speed',
  'qwantify',
  'bitrix link preview',
  'xing-contenttabreceiver',
  'google-inspectiontool',
  'telegrambot',
];

const _isPrerender =
  typeof window !== 'undefined' &&
  BOT_USER_AGENTS.some((bot) =>
    navigator.userAgent.toLowerCase().includes(bot)
  );

export const isPrerender = (): boolean => _isPrerender;

/**
 * Wait for network idle using PerformanceObserver
 * Tracks ALL resources: fetch, XHR, images, scripts, fonts, CSS
 */
export const waitForNetworkIdle = (
  idleMs = 1500,
  maxMs = 15_000
): Promise<void> =>
  new Promise((resolve) => {
    if (typeof PerformanceObserver === 'undefined') {
      setTimeout(resolve, idleMs);
      return;
    }

    let lastActivity = Date.now();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
    };

    const maxTimer = setTimeout(() => {
      finish();
      resolve();
    }, maxMs);

    const observer = new PerformanceObserver(() => {
      lastActivity = Date.now();
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch {
      setTimeout(resolve, idleMs);
      return;
    }

    const check = () => {
      if (done) return;
      if (Date.now() - lastActivity >= idleMs) {
        clearTimeout(maxTimer);
        finish();
        resolve();
      } else {
        setTimeout(check, 200);
      }
    };

    setTimeout(check, 500);
  });

/**
 * Initialize prerender - wait for network idle, then signal ready
 */
export const initPrerender = async (): Promise<void> => {
  if (!isPrerender()) {
    (window as any).prerenderReady = true;
    return;
  }

  await waitForNetworkIdle();
  (window as any).prerenderReady = true;
};
